import { all, apply, put, takeEvery, takeLatest } from 'redux-saga/effects'

import { IActionType, IAxiosResponse, IPaginator } from '../../root.types'
import { IActionLoad, IActionLoadReport, IActionRemove, ICreateState, ReportTypes } from './types'
import {
    loadActiveMinutesFailure,
    loadActiveMinutesSuccess,
    loadComorbiditiesFailure,
    loadComorbiditiesSuccess,
    loadHeartRateFailure,
    loadHeartRateSuccess,
    loadMedicamentsFailure,
    loadMedicamentsSuccess,
    loadStepsFailure,
    loadStepsSuccess,
    loadReportSuccess,
    loadReportFailure,
    removeReportSuccess,
    removeReportFailure,
    loadSleepSuccess,
    loadSleepFailure,
    createReportFailure,
    createReportSuccess,
    loadMoreSuccess,
    loadMoreFailure
} from './actions'
import timeSeriesService from '../../../../services/time.series'
import medicalIndicatorService from '../../../../services/medical.indicator'
import reportService from '../../../../services/report'
import TimeSeries, { TimeSeriesType } from '../../../application/models/time.series/time.series'
import IntradayHeartRate from '../../../application/models/time.series/intraday/heart.rate/intraday.heart.rate'
import moment from 'moment'
import { TimeSeriesIntervalFilter } from '../../../application/models/time.series/filters/interval.filter'
import { TimeSeriesInterval } from '../../../application/models/time.series/intraday/intraday.summary'
import { TimeSeriesSimpleFilter } from '../../../application/models/time.series/filters/simple.filter'
import UtilDatePicker from '../../../../components/date.and.hour/date'
import Report from '../../../application/models/report/report'
import { SnackBarMessageType } from '../../../../components/snackbar'
import { open } from '../../snack.bar/actions'
import sleepService from '../../../../services/sleep'
import { push } from 'connected-react-router'

import Comorbidity from '../../../application/models/medical.indicator/comorbidities'
import { MedicalIndicatorType } from '../../../application/utils/medical.indicator.type'
import Medicament from '../../../application/models/medical.indicator/medicament'

function* loadSteps(action: IActionType<IActionLoadReport>) {
    const { patientId, startDate, endDate, type } = action.payload
    try {
        const filter: TimeSeriesSimpleFilter = new TimeSeriesSimpleFilter()
            .fromJSON({
                start_date: UtilDatePicker.formatDate(startDate, 'YYYY-MM-DD'),
                end_date: UtilDatePicker.formatDate(endDate, 'YYYY-MM-DD')
            })
        const steps: TimeSeries = yield apply(
            timeSeriesService,
            timeSeriesService.getResource,
            [patientId, TimeSeriesType.STEPS, filter]
        )
        yield put(loadStepsSuccess(steps, type))
    } catch (e) {
        yield put(loadStepsFailure(type))
    }
}

function* loadActiveMinutes(action: IActionType<IActionLoadReport>) {
    const { patientId, startDate, endDate, type } = action.payload
    try {
        const filter: TimeSeriesSimpleFilter = new TimeSeriesSimpleFilter()
            .fromJSON({
                start_date: UtilDatePicker.formatDate(startDate, 'YYYY-MM-DD'),
                end_date: UtilDatePicker.formatDate(endDate, 'YYYY-MM-DD')
            })
        const activeMinutes: TimeSeries = yield apply(
            timeSeriesService,
            timeSeriesService.getResource,
            [patientId, TimeSeriesType.ACTIVE_MINUTES, filter]
        )
        yield put(loadActiveMinutesSuccess(activeMinutes, type))
    } catch (e) {
        yield put(loadActiveMinutesFailure(type))
    }
}

function* loadHeartRate(action: IActionType<IActionLoadReport>) {
    const { patientId, startDate, endDate, type } = action.payload
    try {
        let currentDateValue: number = 0
        const endDateStr: number = endDate.getTime()
        const result: IntradayHeartRate[] = []
        let index: number = 0
        do {
            const currentDate = moment(startDate).add(index, 'days').format('YYYY-MM-DD')
            currentDateValue = moment(currentDate, 'YYYY-MM-DD').toDate().getTime()
            const filter: TimeSeriesIntervalFilter = new TimeSeriesIntervalFilter()
                .fromJSON({ date: currentDate, interval: TimeSeriesInterval.ONE_HOUR })
            const hearRate: IntradayHeartRate = yield apply(
                timeSeriesService,
                timeSeriesService.getResourceWithInterval,
                [patientId, TimeSeriesType.HEART_RATE, filter]
            )
            result.push(hearRate)
            index++
        } while (endDateStr > currentDateValue)
        yield put(loadHeartRateSuccess(result, type))
    } catch (e) {
        yield put(loadHeartRateFailure(type))
    }
}
function* loadSleep(action: IActionType<IActionLoadReport>) {
    const { patientId, startDate, endDate, type } = action.payload
    try {
        const filter: TimeSeriesSimpleFilter = new TimeSeriesSimpleFilter()
            .fromJSON({
                start_date: UtilDatePicker.formatDate(startDate, 'YYYY-MM-DD'),
                end_date: UtilDatePicker.formatDate(endDate, 'YYYY-MM-DD')
            })
        const sleeps = yield apply(
            sleepService,
            sleepService.getAll,
            [patientId, undefined, filter]
        )
        yield put(loadSleepSuccess(sleeps.data, type))
    } catch (e) {
        yield put(loadSleepFailure(type))
    }
}
function* create(action: IActionType<ICreateState>) {
    try {
        const { report } = action.payload

        const response: Report = yield apply(
            reportService,
            reportService.create,
            [report]
        )
        yield put(createReportSuccess(response))
        yield put(
            open(
                SnackBarMessageType.SUCCESS,
                '',
                `PATIENTS.REPORT.LIST.REPORT_MESSAGE_CREATED`)
        )
        yield put(push(`/app/patients/${response.patient_id}/report`))

    } catch (e) {
        open(
            SnackBarMessageType.ERROR,
            '',
            `PATIENTS.REPORT.LIST.REPORT_MESSAGE_FAILURE`)
        yield put(createReportFailure())
    }
}

function* loadReport(action: IActionType<IActionLoad>) {
    try {
        const { patientId, paginator, filter } = action.payload
        const report: IAxiosResponse<Report[]> = yield apply(
            reportService,
            reportService.getAll,
            [patientId, paginator, filter]
        )
        yield put(loadReportSuccess(report))
    } catch (e) {
        yield put(loadReportFailure())
    }
}

function* loadMore(action: IActionType<IActionLoad>) {
    try {
        const { patientId, paginator, filter } = action.payload
        const response: IAxiosResponse<Report[]> = yield apply(
            reportService,
            reportService.getAll,
            [patientId, paginator, filter]
        )
        yield put(loadMoreSuccess(response))
    } catch (e) {
        yield put(loadMoreFailure())
    }
}

function* remove(action: IActionType<IActionRemove>) {
    try {
        const { patientId, ids } = action.payload
        for (const ReportId of ids) {
            yield apply(
                reportService,
                reportService.remove,
                [patientId, ReportId]
            )
        }
        yield put(
            open(
                SnackBarMessageType.SUCCESS,
                '',
                `PATIENTS.REPORT.LIST.REPORT_MESSAGE_DELETED`)
        )

        yield put(removeReportSuccess(ids))
    } catch (e) {
        yield put(removeReportFailure())
    }
}

function* loadMedicalIndicators(action: IActionType<IActionLoadReport>) {
    const { patientId } = action.payload
    const paginator: IPaginator = {
        first: 0,
        rows: 1,
        page: 0,
        pageCount: 0,
        totalRecords: 0,
        search: {
            key: '',
            value: ''
        }
    }
    try {
        const comorbidities: IAxiosResponse<Comorbidity[]> = yield apply(
            medicalIndicatorService,
            medicalIndicatorService.getAll,
            [patientId, MedicalIndicatorType.COMORBIDITIES, paginator]
        )
        yield put(loadComorbiditiesSuccess(comorbidities?.data?.length ? comorbidities.data[0] : new Comorbidity()))
    } catch (e) {
        yield put(loadComorbiditiesFailure())
    }
    try {
        const medicaments: IAxiosResponse<Medicament[]> = yield apply(
            medicalIndicatorService,
            medicalIndicatorService.getAll,
            [patientId, MedicalIndicatorType.MEDICAMENTS, paginator]
        )
        yield put(loadMedicamentsSuccess(medicaments?.data?.length ? medicaments.data[0] : new Medicament()))
    } catch (e) {
        yield put(loadMedicamentsFailure())
    }
}


export default function* reportSaga() {
    return yield all([
        takeLatest(ReportTypes.LOAD_STEPS_REQUEST, loadSteps),
        takeLatest(ReportTypes.LOAD_ACTIVE_MINUTES_REQUEST, loadActiveMinutes),
        takeLatest(ReportTypes.LOAD_HEART_RATE_REQUEST, loadHeartRate),
        takeLatest(ReportTypes.LOAD_MEDICAL_INDICATORS_REQUEST, loadMedicalIndicators),
        takeEvery(ReportTypes.LOAD_STEPS_REQUEST, loadSteps),
        takeEvery(ReportTypes.LOAD_ACTIVE_MINUTES_REQUEST, loadActiveMinutes),
        takeEvery(ReportTypes.LOAD_HEART_RATE_REQUEST, loadHeartRate),
        takeEvery(ReportTypes.LOAD_SLEEP_REQUEST, loadSleep),
        takeLatest(ReportTypes.CREATE_REPORT_REQUEST, create),
        takeLatest(ReportTypes.LOAD_REPORT_REQUEST, loadReport),
        takeLatest(ReportTypes.LOAD_MORE_REQUEST, loadMore),
        takeLatest(ReportTypes.REMOVE_REPORT_REQUEST, remove)
    ])
}
